import { AxiosRequestConfig } from "axios";
import React, { useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { SessionContext, SessionContextProps } from "../../Context/SessionContext";
import { Customer, EnhancedCustomer } from "../../Models/Customer";
import { CustomerNote, EnhancedCustomerNote } from "../../Models/CustomerNote";
import { OpportunityNote } from "../../Models/OpportunityNote";
import { Error } from "../../Utils/Errors";
import { zipCodeIsValid } from "../../Utils/Helpers";
import OpsEndpointPathsUtility from "../../Utils/OpsEndpointPathsUtility";
import OpsMappingUtility from "../../Utils/OpsMappingUtility";
import { RequestMethod, useApi } from "../../Utils/useApi";

interface Props {
  sessionComplete: boolean;
}

const SaveData: React.FC<Props> = ({ sessionComplete }: Props) => {
  const {
    customer,
    lead,
    note,
    isSessionShortCircuited,
    phoneNumberErrors,
    saveCustomer: contextSaveCustomer,
    step,
    setIsCustomerValidForOpsBooking,
    setCustomerInfo,
    setDuplicateCustomers,
    setIsSavingCustomer,
    setNote,
    helpers: { saveToLocal, loadFromLocal }
  } = useContext(SessionContext);
  const sessionRef = useRef<
    Pick<SessionContextProps, "customer" | "lead" | "step" | "note" | "isSessionShortCircuited" | "phoneNumberErrors">
  >({
    customer,
    lead,
    note,
    phoneNumberErrors,
    step,
    isSessionShortCircuited
  });
  const [saveCustomer, setSaveCustomer] = useState<EnhancedCustomer>();
  const customerChangedRef = useRef(false);
  const customerConfigRef = useRef<AxiosRequestConfig>();
  const customerValidRef = useRef(false);
  const saveCustomerRef = useRef(customer);

  const [saveNote, setSaveNote] = useState<CustomerNote>();
  const noteChangedRef = useRef(false);
  const fullNoteRef = useRef<CustomerNote>();

  const [saveOppoNote, setSaveOppoNote] = useState<string>();
  const oppoNoteRef = useRef("");
  const prevOppoNoteRef = useRef(loadFromLocal<string>("oppoNote") || "");

  // collect session props which do not have side effects and maintain the ref
  useEffect(() => {
    sessionRef.current.lead = lead;
    sessionRef.current.isSessionShortCircuited = isSessionShortCircuited;
    sessionRef.current.phoneNumberErrors = phoneNumberErrors;
  }, [lead, isSessionShortCircuited, phoneNumberErrors]);

  // ON CONTEXT SAVE CUSTOMER CHANGE
  useEffect(() => {
    // save the customer
    if (contextSaveCustomer) {
      customerConfigRef.current = contextSaveCustomer.override ? { params: { duplicateChecks: false } } : undefined;
      // IF overriding duplicates or forcing an update, clear state before updating to clear useApi dirty protection logic.
      // setTimeout is MANDATORY to prevent React from batching the events into a single update.
      if (contextSaveCustomer.override || contextSaveCustomer.forceUpdate) {
        setSaveCustomer(undefined);
        setTimeout(() => setSaveCustomer({ ...contextSaveCustomer.customer }), 0);
      }
      // ELSE execute a single state update
      else setSaveCustomer({ ...contextSaveCustomer.customer });
    }
  }, [contextSaveCustomer]);

  // ON STEP CHANGE
  useEffect(() => {
    // trigger save IF
    if (
      // NOT on page refresh (refs must have mounted)
      sessionRef.current.step &&
      // AND if the step allows saving
      step.isSaveStep &&
      // AND NOT when the session is 'short-circuited' or arriving step is not a short-circuit step
      (!sessionRef.current.isSessionShortCircuited || !step.isShortCircuit) &&
      // AND zipcode is valid
      (!sessionRef.current.customer.zipCode || zipCodeIsValid(sessionRef.current.customer.zipCode)) &&
      // AND phone numbers are valid
      !sessionRef.current.phoneNumberErrors.homePhoneNumber &&
      !sessionRef.current.phoneNumberErrors.phoneNumber &&
      !sessionRef.current.phoneNumberErrors.workPhoneNumber
    ) {
      // IF note has a state to save AND customer has an id, save it
      if (fullNoteRef.current && sessionRef.current.customer.id) {
        setSaveNote(fullNoteRef.current);
        fullNoteRef.current = undefined;
        noteChangedRef.current = false;
      }
      // IF Customer has changes AND primaries are valid, save it
      if (customerChangedRef.current && customerValidRef.current) setSaveCustomer({ ...sessionRef.current.customer });
    }
    sessionRef.current.step = step;
  }, [step]);

  // on session Complete
  useEffect(() => {
    if (sessionComplete) {
      // if final customer exists and was changed
      if (sessionRef.current.customer.id && customerChangedRef.current)
        if (!customerValidRef.current)
          // but if primaries are not valid, toast error
          toast.error(
            "Final guest changes were not saved. Please go back and correct missing contact information or resolve duplicate conflicts.",
            { autoClose: false, toastId: "invalidFinalCustomer" }
          );
        else {
          // else save the customer
          toast.dismiss("invalidFinalCustomer");
          setSaveCustomer({ ...sessionRef.current.customer });
        }
      // IF note has a state to save AND customer has an id, save it
      if (fullNoteRef.current && sessionRef.current.customer.id) {
        setSaveNote(fullNoteRef.current);
        fullNoteRef.current = undefined;
        noteChangedRef.current = false;
      }
      // save the opportunity note
      if (
        // IF customer has an opportunity id
        sessionRef.current.customer.opportunity?.id &&
        // AND the value has changed from last submission
        prevOppoNoteRef.current !== oppoNoteRef.current
      )
        setSaveOppoNote(oppoNoteRef.current);
    }
  }, [sessionComplete]);

  // on customer change
  useEffect(() => {
    customerChangedRef.current =
      EnhancedCustomer.isChange(saveCustomerRef.current, customer) ||
      // if the opportunity has not been fully created, treat it as a change
      !customer.opportunity?.id;
    customerValidRef.current = EnhancedCustomer.isValid(customer);
    sessionRef.current.customer = customer;
    customerConfigRef.current = undefined;
  }, [customer]);

  // on note change
  useEffect(() => {
    // flag if a manual change was made from the last saved state
    noteChangedRef.current = EnhancedCustomerNote.isChange(sessionRef.current.note, note);
    // note is valid if
    if (
      sessionRef.current.note.id === null ||
      // it contains manual text or a non-hidden note tag
      ((note.text || sessionRef.current.note.text || note.tags.length) &&
        // it has a center
        (!!note.centerId || sessionRef.current.note.centerId) &&
        // it has a change
        noteChangedRef.current)
    ) {
      sessionRef.current.note = note;
      fullNoteRef.current = EnhancedCustomerNote.toFullBase(note);
      oppoNoteRef.current = fullNoteRef.current.text;
    }
  }, [note]);

  useEffect(() => {
    if (saveCustomer) {
      customerChangedRef.current = false;
      saveCustomerRef.current = saveCustomer;
      saveToLocal(saveCustomer, "saveCustomer");
    }
  }, [saveCustomer, saveToLocal]);
  const {
    response: customerResponse,
    isLoading: customerLoading,
    error
  } = useApi<Customer, Customer>({
    method: saveCustomer?.id ? "put" : "post",
    url: saveCustomer?.id
      ? OpsEndpointPathsUtility.aspirePutCustomerUrl(lead.id, saveCustomer.id)
      : OpsEndpointPathsUtility.aspirePostCustomerUrl(lead.id),
    data: saveCustomer,
    configRef: customerConfigRef,
    valid: !!saveCustomer,
    error: saveCustomer?.id ? Error.PutCustomer : Error.PostCustomer
  });
  useEffect(() => setIsSavingCustomer(customerLoading), [customerLoading]);
  // ON CUSTOMER SAVE SUCCESS
  useEffect(() => {
    if (customerResponse) {
      setIsCustomerValidForOpsBooking(OpsMappingUtility.isCustomerValidForBooking(customerResponse));
      if (!toast.isActive("saveCustomerSuccessful")) {
        toast.dismiss("invalidCustomerSubmit");
        toast.success(`Successfully ${sessionRef.current.customer.id ? "Updated" : "Created"} Guest!`, {
          toastId: "saveCustomerSuccessful"
        });
      }
      // if missing props from the returned guest, update the session customer
      const updates: Partial<EnhancedCustomer> = {};
      if (!sessionRef.current.customer.opportunity?.id) updates.opportunity = customerResponse.opportunity;
      if (!sessionRef.current.customer.id) {
        updates.id = customerResponse.id;
        updates.externalId = customerResponse.externalId;
      }
      if (!sessionRef.current.customer.code) updates.code = customerResponse.code;
      if (Object.keys(updates).length) {
        saveCustomerRef.current = { ...saveCustomerRef.current, ...updates };
        setCustomerInfo(updates);
      }
    }
  }, [customerResponse, setCustomerInfo]);
  // ON CUSTOMER SAVE ERROR
  useEffect(() => {
    if (error?.response?.status === 409) {
      // if duplicates were found, set up the duplicate resolution process
      setDuplicateCustomers(error.response.data.data as Customer[]);
    }
  }, [error, setDuplicateCustomers]);

  const noteUrlRef = useRef("");
  const noteMethodRef = useRef<RequestMethod>("post");
  const noteErrorRef = useRef(Error.PostCustomerNote);
  useEffect(() => {
    noteUrlRef.current = `Legacy/V0/contacts/${lead.id}/customers/${customer.id}/notes${note.id ? `/${note.id}` : ""}`;
    noteMethodRef.current = note.id ? "put" : "post";
    noteErrorRef.current = note.id ? Error.PutCustomerNote : Error.PostCustomerNote;
  }, [lead.id, customer.id, note.id]);
  const { response: noteResponse } = useApi<CustomerNote, CustomerNote>({
    methodRef: noteMethodRef,
    urlRef: noteUrlRef,
    data: saveNote,
    valid: !!saveNote,
    errorRef: noteErrorRef
  });
  // ON NOTE SAVE SUCCESS
  useEffect(() => {
    if (!noteResponse) return;
    if (!toast.isActive("saveNoteSuccessful"))
      toast.success("Successfully Updated Guest Note!", { toastId: "saveNoteSuccessful" });
    if (!sessionRef.current.note.id) setNote(note => ({ ...note, id: noteResponse.id }));
  }, [noteResponse, setNote]);

  // ON SAVE OPPO NOTE START
  useEffect(() => {
    if (!saveOppoNote) return;
    prevOppoNoteRef.current = saveOppoNote;
    saveToLocal(prevOppoNoteRef.current, "oppoNote");
  }, [saveOppoNote, saveToLocal]);
  useApi<boolean, OpportunityNote>({
    method: "post",
    url: `Legacy/V0/contacts/${lead.id}/opportunities/${sessionRef.current.customer.opportunity?.id}/notes`,
    data: { note: saveOppoNote } as OpportunityNote,
    valid: !!saveOppoNote,
    error: Error.PostOpportunityNote
  });

  return <></>;
};

export default SaveData;
